import { useApolloClient } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  useCreateFamily,
  useSearchApplicantDuplicatesKey,
  useSearchApplicantsKey,
  useSearchParentsKey,
} from "src/generated/hooks/form-service/queries";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import { canonicalPhoneNumber } from "src/services/format";
import * as Url from "src/services/url";
import {
  NewGuardianEntry,
  useGuardianEntries,
} from "./components/useGuardianEntries";
import { GET_STUDENTS } from "./graphql/queries";
import {
  INITIAL_STUDENT_PROFILE,
  StudentProfileForm,
  StudentProfileValidator,
} from "./schemas";

/**
 * Holds all state, data fetching, and submit handling for the create-student
 * scene so the component is just the view. Prefills the name fields from the
 * picker's create-new navigation state when present.
 */
export function useNewStudent() {
  const { glossary } = useGlossary();
  const [submitting, setSubmitting] = React.useState(false);
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const userPermissions = useUserPermissions();
  const { isSchoolAdmin } = useSchoolAdmin();

  const { mutateAsync: createFamily } = useCreateFamily();
  const guardians = useGuardianEntries();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  // When the user picks a student from the possible-duplicates panel, we link the
  // guardians to that existing student instead of creating a new one. The create
  // form is replaced by a read-only chip, and this student is omitted from the
  // createFamily students array (referenced by id in the relationships only).
  const [selectedExisting, setSelectedExisting] =
    React.useState<ApplicantDuplicate | null>(null);

  // The picker's create-new action carries context in the URL (not navigation
  // state) so it survives a page reload. Reading it live is safe: the route's
  // param hooks mutate-and-preserve existing params, so these are not stripped.
  const prefillFirstName = searchParams.get("prefillFirstName") ?? "";
  const prefillLastName = searchParams.get("prefillLastName") ?? "";
  const returnFormTemplateId = searchParams.get("returnFormTemplateId");
  const returnParams = searchParams.get("returnParams");
  const returnSearchText = searchParams.get("returnSearchText") ?? "";
  // Present only when the flow started from the Create-new-form dialog; used to
  // return there after save (student preselected) or on Back (search text kept).
  const returnToCreateForm =
    returnFormTemplateId !== null && returnParams !== null
      ? {
          formTemplateId: returnFormTemplateId,
          params: returnParams,
          searchText: returnSearchText,
        }
      : undefined;
  const initialValues: StudentProfileForm = React.useMemo(
    () => ({
      ...INITIAL_STUDENT_PROFILE,
      first_name: prefillFirstName || INITIAL_STUDENT_PROFILE.first_name,
      last_name: prefillLastName || INITIAL_STUDENT_PROFILE.last_name,
    }),
    [prefillFirstName, prefillLastName]
  );

  const handleSubmit = async (form: StudentProfileForm) => {
    organization.do(async (org) => {
      setSubmitting(true);
      try {
        // Existing student (picked from duplicates): link only, no profile parse
        // and no student insert. New student: parse the form and insert it.
        const parsed = selectedExisting
          ? null
          : StudentProfileValidator.parse(form);
        const studentId = selectedExisting?.id ?? crypto.randomUUID();
        const studentName = selectedExisting
          ? {
              first_name: selectedExisting.first_name,
              last_name: selectedExisting.last_name,
            }
          : { first_name: parsed!.first_name, last_name: parsed!.last_name };
        // For an existing student we do not have its address, so a new guardian
        // that opts into "use student address" falls back to empty values.
        const studentAddress = {
          street_address: parsed?.street_address ?? null,
          street_address_line_2: parsed?.street_address_line_2 ?? null,
          city: parsed?.city ?? null,
          state: parsed?.state ?? null,
          zip_code: parsed?.zip_code ?? null,
        };

        // Only new guardians are created; existing ones are linked by id via the
        // relationships below. Guardian is first_person, student is second_person.
        const guardianPayload = guardians.entries
          .filter((entry): entry is NewGuardianEntry => entry.kind === "new")
          .map((entry) => ({
            id: entry.id,
            first_name: entry.firstName,
            middle_name: entry.middleName,
            last_name: entry.lastName,
            email_address: entry.emailAddress,
            // Normalize any entered format to E.164 before transmitting.
            phone_number: entry.phoneNumber
              ? canonicalPhoneNumber(entry.phoneNumber) || null
              : null,
            reference_id: entry.referenceId,
            ...(entry.useStudentAddress
              ? studentAddress
              : {
                  street_address: entry.streetAddress,
                  street_address_line_2: entry.streetAddressLine2,
                  city: entry.city,
                  state: entry.state,
                  zip_code: entry.zipCode,
                }),
          }));
        const relationshipPayload = guardians.entries.map((entry) => ({
          first_person: entry.id,
          second_person: studentId,
        }));

        await createFamily({
          body: {
            data: {
              organizationId: org.id,
              // Omit the student when linking to an existing one; it is referenced
              // by id in the relationships above.
              students: parsed
                ? [
                    {
                      id: studentId,
                      first_name: parsed.first_name,
                      middle_name: parsed.middle_name ?? null,
                      last_name: parsed.last_name,
                      birth_date: parsed.birth_date ?? null,
                      reference_id: parsed.reference_id ?? null,
                      ...studentAddress,
                    },
                  ]
                : [],
              guardians: guardianPayload,
              relationships: relationshipPayload,
            },
          },
        });

        // Refresh person-derived caches after the out-of-band family create.
        // refetchQueries updates any mounted (PreserveState-frozen) list observer;
        // evicting `person` + gc also clears lists that are not currently mounted
        // (e.g. arriving from the create-form dialog), so they refetch on next view
        // instead of serving stale cache. A family create can add both a student
        // and a guardian, so this covers the students and parents lists.
        await apolloClient.refetchQueries({
          include: [GET_STUDENTS],
          updateCache: (cache) => {
            cache.evict({ fieldName: "person" });
            cache.gc();
          },
        });
        // The possible-duplicates panels and person pickers read react-query person
        // searches; invalidate them so the new records appear without a refresh.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [useSearchApplicantsKey] }),
          queryClient.invalidateQueries({
            queryKey: [useSearchApplicantDuplicatesKey],
          }),
          queryClient.invalidateQueries({ queryKey: [useSearchParentsKey] }),
        ]);

        toast({
          title: selectedExisting
            ? glossary`Parent/guardian added to student`
            : glossary`Student created successfully`,
          status: "success",
          isClosable: true,
        });

        if (returnToCreateForm) {
          // Return to the forms list and reopen the create-form dialog with the
          // student (new or existing) preselected. The reopen context rides in the
          // URL query (same pattern as the forward hop) so it survives a reload and
          // is not dropped by the forms list's own setSearchParams calls.
          const params = new URLSearchParams(returnToCreateForm.params);
          params.set("reopenStudentId", studentId);
          params.set("reopenStudentFirstName", studentName.first_name);
          params.set("reopenStudentLastName", studentName.last_name);
          navigate(
            Url.OrgAdmin.Forms.index({
              organization: org,
              formTemplateId: returnToCreateForm.formTemplateId,
              params: params.toString(),
            })
          );
        } else {
          // School admins (incl. elevated) may lack edit access to a student with
          // no form at their school, so send them to the list, not the edit page.
          navigate(
            isSchoolAdmin
              ? Url.OrgAdmin.Students.index(org)
              : Url.OrgAdmin.Students.edit(org, studentId)
          );
        }
      } catch (err) {
        console.error(err);
        toast.error({ title: glossary`Error adding student` });
      } finally {
        setSubmitting(false);
      }
    });
  };

  const canCreateUser = userPermissions.hasOne("user:create");
  const organizationId = React.useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );
  // Back returns to the origin: the forms list (reopening the dialog with the
  // original search text, via the same query-param pattern as save) when the flow
  // came from there, else the Students list.
  const cancelHref = organization
    .map((org) => {
      if (!returnToCreateForm) return Url.OrgAdmin.Students.index(org);
      const params = new URLSearchParams(returnToCreateForm.params);
      params.set("reopenSearchText", returnToCreateForm.searchText);
      return Url.OrgAdmin.Forms.index({
        organization: org,
        formTemplateId: returnToCreateForm.formTemplateId,
        params: params.toString(),
      });
    })
    .withDefault("#");

  return {
    organization,
    organizationId,
    canCreateUser,
    initialValues,
    isSubmitting: submitting,
    onSubmit: handleSubmit,
    cancelHref,
    // True when this flow came from the create-form dialog, so the primary action
    // continues back to that form rather than just saving the family.
    returnsToForm: returnToCreateForm != null,
    guardians,
    selectedExisting,
    onSelectExisting: setSelectedExisting,
    onClearExisting: () => setSelectedExisting(null),
  };
}
